import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { PATIENT_BEARING_MODULES } from '@his/shared';
import type { MealSheetRow, NpsSummary } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { visibleInterval } from '@/lib/polling';
import { cn, dateOnly, dateTime, humanize, money } from '@/lib/utils';
import { statusLabel } from '@/components/ui/badge';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Badge, Button, Checkbox, Field, Input, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

// ── Ambulance (SRS 10.6) ─────────────────────────────────────────────────────

interface Trip {
  id: string;
  pickup: string;
  dropoff: string;
  distanceKm: number;
  chargeMinor: number;
  invoiceId: string | null;
  startedAt: string;
  endedAt: string | null;
  vehicle: { id: string; registrationNo: string; driverName: string | null };
}

export function AmbulancePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [ending, setEnding] = useState<Trip | null>(null);
  const [form, setForm] = useState({ distanceKm: '', charge: '', bill: true });

  const vehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () =>
      get<{ id: string; registrationNo: string; type: string; driverName: string | null; isActive: boolean }[]>(
        '/ambulance/vehicles',
      ),
  });

  const trips = useQuery({
    queryKey: ['trips'],
    queryFn: () => get<Trip[]>('/ambulance/trips?limit=100'),
  });

  const end = useMutation({
    mutationFn: () =>
      put(`/ambulance/trips/${ending?.id}/end`, {
        distanceKm: Number(form.distanceKm || 0),
        chargeMinor: Math.round(Number(form.charge || 0) * 100),
        bill: form.bill,
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'support_trip_closed'));
      void queryClient.invalidateQueries({ queryKey: ['trips'] });
      setEnding(null);
      setForm({ distanceKm: '', charge: '', bill: true });
    },
    onError: (error) => toast.error(tr('admin', 'support_could_not_close_trip'), describeError(error)),
  });

  const open = trips.data?.filter((trip) => !trip.endedAt) ?? [];

  return (
    <>
      <PageHeader
        title={tr('admin', 'support_ambulance')}
        description={tr('admin', 'support_ambulance_hint')}
        actions={<Button onClick={() => navigate('/ambulance/trips/new')}>{tr('admin', 'support_dispatch')}</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label={tr('admin', 'support_vehicles')} value={vehicles.data?.filter((v) => v.isActive).length ?? 0} />
        <StatTile label={tr('admin', 'support_out_on_a_trip')} value={open.length} tone={open.length ? 'warning' : 'default'} />
        <StatTile label={tr('admin', 'support_trips_logged')} value={trips.data?.length ?? 0} />
      </div>

      <DataTable
        rows={trips.data}
        loading={trips.isLoading} refreshing={trips.isFetching}
        error={trips.error}
        rowKey={(row) => row.id}
        empty={tr('admin', 'support_no_trips_logged')}
        columns={[
          { header: tr('admin', 'support_vehicle'), sort: (row) => row.vehicle.registrationNo, cell: (row) => row.vehicle.registrationNo },
          { header: tr('admin', 'support_driver'), sort: (row) => row.vehicle.driverName, cell: (row) => row.vehicle.driverName ?? '—' },
          { header: tr('admin', 'support_trip_from'), sort: (row) => row.pickup, cell: (row) => row.pickup },
          { header: tr('admin', 'support_trip_to'), sort: (row) => row.dropoff, cell: (row) => row.dropoff },
          { header: tr('admin', 'support_started'), sort: (row) => row.startedAt, cell: (row) => dateTime(row.startedAt) },
          {
            header: tr('admin', 'support_distance'),
            align: 'right',
            sort: (row) => (row.endedAt ? row.distanceKm : null),
            cell: (row) => (row.endedAt ? tr('admin', 'support_distance_km', { distance: row.distanceKm }) : '—'),
          },
          { header: tr('admin', 'support_charge'), align: 'right', sort: (row) => row.chargeMinor, cell: (row) => (row.chargeMinor ? money(row.chargeMinor) : '—') },
          {
            header: tr('admin', 'status'),
            sort: (row) => (row.endedAt ? (row.invoiceId ? 'billed' : 'closed') : 'on trip'),
            cell: (row) =>
              row.endedAt ? (
                <Badge variant="success">
                  {row.invoiceId ? tr('admin', 'support_trip_billed') : tr('admin', 'support_trip_closed_badge')}
                </Badge>
              ) : (
                <Badge variant="warning">{tr('admin', 'support_on_trip')}</Badge>
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              !row.endedAt && (
                <Button size="sm" onClick={() => setEnding(row)}>
                  {tr('admin', 'support_end_trip')}
                </Button>
              ),
          },
        ]}
      />

      <Dialog
        open={Boolean(ending)}
        onClose={() => setEnding(null)}
        title={tr('admin', 'support_end_the_trip')}
        description={ending ? `${ending.pickup} → ${ending.dropoff}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnding(null)}>
              {tr('admin', 'cancel')}
            </Button>
            <Button onClick={() => end.mutate()} loading={end.isPending}>
              {tr('admin', 'support_close_trip')}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('admin', 'support_distance_km_label')} required>
            <Input
              type="number"
              step="0.1"
              min={0}
              value={form.distanceKm}
              onChange={(e) => setForm({ ...form, distanceKm: e.target.value })}
            />
          </Field>
          <Field label={tr('admin', 'support_charge')}>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={form.charge}
              onChange={(e) => setForm({ ...form, charge: e.target.value })}
            />
          </Field>
          <Checkbox
            className="sm:col-span-2"
            checked={form.bill}
            onCheckedChange={(checked) => setForm({ ...form, bill: checked })}
            label={tr('admin', 'support_bill_trip_to_patient')}
          />
        </div>
      </Dialog>
    </>
  );
}


// ── Dietary (SRS 10.7) ───────────────────────────────────────────────────────

export function DietaryPage() {
  const [wardId, setWardId] = useState('');

  const wards = useQuery({
    queryKey: ['wards'],
    queryFn: () => get<{ id: string; name: string }[]>('/ipd/wards'),
  });

  const sheet = useQuery({
    queryKey: ['meal-sheet', wardId],
    queryFn: () => get<MealSheetRow[]>(`/dietary/meal-sheet${qs({ wardId: wardId || undefined })}`),
  });

  const byDiet = new Map<string, number>();
  for (const row of sheet.data ?? []) byDiet.set(row.dietType, (byDiet.get(row.dietType) ?? 0) + 1);

  return (
    <>
      <PageHeader title={tr('admin', 'support_kitchen_sheet')} description={tr('admin', 'support_kitchen_sheet_hint')} />

      <div className="mb-4 flex flex-wrap gap-2">
        {[...byDiet.entries()].map(([diet, count]) => (
          <div key={diet} className="rounded-lg border bg-card px-4 py-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{humanize(diet)}</p>
            <p className="text-xl font-semibold tabular">{count}</p>
          </div>
        ))}
      </div>

      <SearchFilterBar
        filters={
          <FilterField label={tr('admin', 'ward')}>
            <Select value={wardId} onChange={(e) => setWardId(e.target.value)} className="w-52">
              <option value="">{tr('admin', 'support_all_wards')}</option>
              {wards.data?.map((ward) => (
                <option key={ward.id} value={ward.id}>
                  {ward.name}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={wardId ? () => setWardId('') : undefined}
      />

      <DataTable
        rows={sheet.data}
        loading={sheet.isLoading} refreshing={sheet.isFetching}
        error={sheet.error}
        rowKey={(row) => row.admissionId}
        empty={tr('admin', 'support_no_inpatients_to_feed')}
        columns={[
          { header: tr('admin', 'ward'), sort: (row) => row.wardName, cell: (row) => row.wardName },
          { header: tr('admin', 'bed'), sort: (row) => row.bedCode, cell: (row) => row.bedCode ?? '—' },
          {
            header: tr('admin', 'support_patient'),
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patientName}</p>
                <p className="text-xs text-muted-foreground">{row.mrn}</p>
              </div>
            ),
          },
          {
            header: tr('admin', 'support_diet'),
            sort: (row) => row.dietType,
            cell: (row) => (
              <Badge variant={row.dietType === 'npo' ? 'destructive' : 'default'}>{humanize(row.dietType)}</Badge>
            ),
          },
          {
            header: tr('admin', 'support_allergies'),
            cell: (row) =>
              row.allergies.length ? (
                <span className="inline-flex items-center gap-1 font-medium text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" /> {row.allergies.join(', ')}
                </span>
              ) : (
                '—'
              ),
          },
          { header: tr('admin', 'support_note'), sort: (row) => row.notes, cell: (row) => row.notes ?? '' },
        ]}
      />
    </>
  );
}

// ── Housekeeping (SRS 10.8) ──────────────────────────────────────────────────

export function HousekeepingPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');

  const tasks = useQuery({
    queryKey: ['housekeeping', status],
    queryFn: () =>
      get<
        {
          id: string;
          type: string;
          status: string;
          createdAt: string;
          bed: { code: string | null; ward: string | null; status: string | null } | null;
        }[]
      >(`/housekeeping/tasks${qs({ status: status || undefined, limit: 200 })}`),
    refetchInterval: visibleInterval(60_000),
  });

  const update = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => put(`/housekeeping/tasks/${id}`, { status: next }),
    onSuccess: (_, variables) => {
      toast.success(variables.next === 'done' ? tr('admin', 'support_bed_back_on_board') : tr('admin', 'support_updated'));
      void queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
    },
    onError: (error) => toast.error(tr('admin', 'support_could_not_update_task'), describeError(error)),
  });

  return (
    <>
      <PageHeader title={tr('admin', 'support_housekeeping')} description={tr('admin', 'support_housekeeping_hint')} />

      <SearchFilterBar
        filters={
          <FilterField label={tr('admin', 'status')}>
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{tr('admin', 'support_all')}</option>
              {['pending', 'in_progress', 'done', 'cancelled'].map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status !== 'pending' ? () => setStatus('pending') : undefined}
      />

      <DataTable
        rows={tasks.data}
        loading={tasks.isLoading} refreshing={tasks.isFetching}
        error={tasks.error}
        rowKey={(row) => row.id}
        empty={tr('admin', 'support_nothing_outstanding')}
        columns={[
          { header: tr('admin', 'support_task'), sort: (row) => row.type, cell: (row) => humanize(row.type, 'task_type') },
          { header: tr('admin', 'ward'), sort: (row) => row.bed?.ward, cell: (row) => row.bed?.ward ?? '—' },
          { header: tr('admin', 'bed'), sort: (row) => row.bed?.code, cell: (row) => row.bed?.code ?? '—' },
          { header: tr('admin', 'support_raised'), sort: (row) => row.createdAt, cell: (row) => dateTime(row.createdAt) },
          { header: tr('admin', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status !== 'done' && (
                <div className="flex justify-end gap-1">
                  {row.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => update.mutate({ id: row.id, next: 'in_progress' })}>
                      {tr('admin', 'support_start')}
                    </Button>
                  )}
                  <Button size="sm" onClick={() => update.mutate({ id: row.id, next: 'done' })}>
                    {tr('admin', 'support_done')}
                  </Button>
                </div>
              ),
          },
        ]}
      />
    </>
  );
}

// ── Feedback and complaints (SRS 10.9) ───────────────────────────────────────

export function FeedbackPage() {
  const { can, hasModule } = useAuth();
  const treatsPatients = PATIENT_BEARING_MODULES.some(hasModule);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(() => params.get('tab') ?? 'nps');
  const [resolving, setResolving] = useState<{ id: string; subject: string } | null>(null);
  const [resolution, setResolution] = useState('');

  const from = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const to = new Date().toISOString();

  const nps = useQuery({
    queryKey: ['nps'],
    queryFn: () => get<NpsSummary>(`/feedback/nps?from=${from}&to=${to}`),
  });

  const feedback = useQuery({
    queryKey: ['feedback-list'],
    queryFn: () =>
      get<{ id: string; source: string; npsScore: number | null; rating: number | null; comments: string | null; department: string | null; createdAt: string }[]>(
        `/feedback?from=${from}&to=${to}`,
      ),
    enabled: tab === 'nps',
  });

  const complaints = useQuery({
    queryKey: ['complaints'],
    queryFn: () =>
      get<
        {
          id: string;
          subject: string;
          body: string;
          status: string;
          slaDueAt: string | null;
          slaBreached: boolean;
          createdAt: string;
          resolution: string | null;
        }[]
      >('/feedback/complaints?limit=200'),
    enabled: tab === 'complaints',
  });

  const update = useMutation({
    mutationFn: () => put(`/feedback/complaints/${resolving?.id}`, { status: 'resolved', resolution }),
    onSuccess: () => {
      toast.success(tr('admin', 'support_complaint_resolved'));
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      setResolving(null);
      setResolution('');
    },
    onError: (error) => toast.error(tr('admin', 'support_could_not_resolve'), describeError(error)),
  });

  return (
    <>
      <PageHeader
        title={tr('admin', 'support_feedback_and_complaints')}
        description={tr('admin', treatsPatients ? 'support_feedback_hint_patients' : 'support_feedback_hint_customers')}
        actions={
          can('feedback.manage') && (
            <Button onClick={() => navigate('/feedback/new')}>{tr('admin', 'support_log_a_complaint')}</Button>
          )
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'nps', label: tr('admin', 'support_feedback') },
          { id: 'complaints', label: tr('admin', 'support_complaints') },
        ]}
      />

      {tab === 'nps' && (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label={tr('admin', 'support_net_promoter_score')}
              value={nps.data?.nps ?? 0}
              sub={tr('admin', 'support_last_30_days')}
              tone={(nps.data?.nps ?? 0) >= 30 ? 'success' : (nps.data?.nps ?? 0) < 0 ? 'destructive' : 'warning'}
            />
            <StatTile label={tr('admin', 'support_responses')} value={nps.data?.responses ?? 0} />
            <StatTile label={tr('admin', 'support_promoters')} value={nps.data?.promoters ?? 0} tone="success" />
            <StatTile label={tr('admin', 'support_detractors')} value={nps.data?.detractors ?? 0} tone="destructive" />
          </div>

          <DataTable
            rows={feedback.data}
            loading={feedback.isLoading} refreshing={feedback.isFetching}
            error={feedback.error}
            rowKey={(row) => row.id}
            empty={tr('admin', 'support_no_feedback_30_days')}
            columns={[
              { header: tr('admin', 'when'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
              { header: tr('admin', 'support_source'), sort: (row) => row.source, cell: (row) => <Badge variant="secondary">{humanize(row.source)}</Badge> },
              { header: tr('admin', 'support_nps'), align: 'right', sort: (row) => row.npsScore, cell: (row) => row.npsScore ?? '—' },
              { header: tr('admin', 'support_rating'), align: 'right', sort: (row) => row.rating, cell: (row) => (row.rating ? `${row.rating}/5` : '—') },
              { header: tr('admin', 'department'), sort: (row) => row.department, cell: (row) => row.department ?? '—' },
              { header: tr('admin', 'support_comment'), sort: (row) => row.comments, cell: (row) => row.comments ?? '' },
            ]}
          />
        </>
      )}

      {tab === 'complaints' && (
        <DataTable
          rows={complaints.data}
          loading={complaints.isLoading} refreshing={complaints.isFetching}
          error={complaints.error}
          rowKey={(row) => row.id}
          empty={tr('admin', 'support_no_complaints_logged')}
          columns={[
            { header: tr('admin', 'support_raised'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
            { header: tr('admin', 'support_subject'), sort: (row) => row.subject, cell: (row) => row.subject },
            {
              header: tr('admin', 'support_sla'),
              sort: (row) => row.slaDueAt,
              cell: (row) =>
                row.slaBreached ? (
                  <span className="font-medium text-destructive">{tr('admin', 'support_sla_breached')}</span>
                ) : row.slaDueAt ? (
                  dateTime(row.slaDueAt)
                ) : (
                  '—'
                ),
            },
            { header: tr('admin', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
            { header: tr('admin', 'support_resolution'), sort: (row) => row.resolution, cell: (row) => row.resolution ?? '' },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                !['resolved', 'closed'].includes(row.status) &&
                can('feedback.manage') && (
                  <Button size="sm" onClick={() => setResolving({ id: row.id, subject: row.subject })}>
                    {tr('admin', 'support_resolve')}
                  </Button>
                ),
            },
          ]}
        />
      )}

      <Dialog
        open={Boolean(resolving)}
        onClose={() => setResolving(null)}
        title={tr('admin', 'support_resolve_the_complaint')}
        description={resolving?.subject}
        footer={
          <>
            <Button variant="ghost" onClick={() => setResolving(null)}>
              {tr('admin', 'cancel')}
            </Button>
            <Button onClick={() => update.mutate()} loading={update.isPending} disabled={resolution.trim().length < 3}>
              {tr('admin', 'support_resolve')}
            </Button>
          </>
        }
      >
        <Field label={tr('admin', 'support_what_was_done')} required hint={tr('admin', 'support_resolution_required')}>
          <Textarea rows={4} value={resolution} onChange={(e) => setResolution(e.target.value)} />
        </Field>
      </Dialog>
    </>
  );
}


// ── Tasks (SRS 10.10) ────────────────────────────────────────────────────────

export function TasksPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mine, setMine] = useState(true);
  const [status, setStatus] = useState('pending');

  const tasks = useQuery({
    queryKey: ['tasks', mine, status],
    queryFn: () =>
      get<
        {
          id: string;
          title: string;
          description: string | null;
          type: string;
          priority: string;
          status: string;
          dueAt: string | null;
          createdAt: string;
        }[]
      >(`/tasks${qs({ mine: mine ? 'true' : undefined, status: status || undefined, limit: 200 })}`),
    refetchInterval: visibleInterval(60_000),
  });

  const update = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => put(`/tasks/${id}`, { status: next }),
    onSuccess: () => {
      toast.success(tr('admin', 'support_task_updated'));
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => toast.error(tr('admin', 'support_could_not_update_task'), describeError(error)),
  });

  return (
    <>
      <PageHeader
        title={tr('admin', 'support_tasks')}
        description={tr('admin', 'support_tasks_hint')}
        actions={
          can('task.manage') && <Button onClick={() => navigate('/tasks/new')}>{tr('admin', 'support_assign_a_task')}</Button>
        }
      />

      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('admin', 'support_assigned_to')}>
              <Select className="w-40" value={mine ? 'mine' : 'all'} onChange={(e) => setMine(e.target.value === 'mine')}>
                <option value="mine">{tr('admin', 'support_mine')}</option>
                <option value="all">{tr('admin', 'support_everyone')}</option>
              </Select>
            </FilterField>
            <FilterField label={tr('admin', 'status')}>
              <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{tr('admin', 'support_all')}</option>
                {['pending', 'in_progress', 'done', 'cancelled'].map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        onReset={
          !mine || status !== 'pending'
            ? () => {
                setMine(true);
                setStatus('pending');
              }
            : undefined
        }
      />

      <DataTable
        rows={tasks.data}
        loading={tasks.isLoading} refreshing={tasks.isFetching}
        error={tasks.error}
        rowKey={(row) => row.id}
        empty={tr('admin', 'support_nothing_on_your_list')}
        columns={[
          {
            header: tr('admin', 'support_task'),
            sort: (row) => row.title,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.title}</p>
                {row.description && <p className="text-xs text-muted-foreground">{row.description}</p>}
              </div>
            ),
          },
          { header: tr('admin', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
          {
            header: tr('admin', 'support_priority'),
            sort: (row) => row.priority,
            cell: (row) => (
              <Badge variant={row.priority === 'urgent' ? 'destructive' : row.priority === 'high' ? 'warning' : 'muted'}>
                {humanize(row.priority, 'priority')}
              </Badge>
            ),
          },
          {
            header: tr('admin', 'support_due'),
            sort: (row) => row.dueAt,
            cell: (row) =>
              row.dueAt ? (
                <span className={cn(new Date(row.dueAt) < new Date() && row.status !== 'done' && 'text-destructive')}>
                  {dateTime(row.dueAt)}
                </span>
              ) : (
                '—'
              ),
          },
          { header: tr('admin', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status !== 'done' && (
                <div className="flex justify-end gap-1">
                  {row.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => update.mutate({ id: row.id, next: 'in_progress' })}>
                      {tr('admin', 'support_start')}
                    </Button>
                  )}
                  <Button size="sm" onClick={() => update.mutate({ id: row.id, next: 'done' })}>
                    {tr('admin', 'support_done')}
                  </Button>
                </div>
              ),
          },
        ]}
      />

    </>
  );
}

